import { ReactNode, useEffect, useState } from "react";
import { LayoutFields } from "../../../../model/SPAModels/campaigns/campaignConfig.Model";
import { DatePicker, Icon, Select, TextField, Option, TextArea, Tooltip, Checkbox, Callout } from "../../SpaAdminScope";
import { RequestStatus } from "../../../../model/ServiceRequestStatus.model";
import { Helper } from "../../utils/Help";
import { CAMPAIGN_APPROVED_BY_FIELD, CAMPAIGN_TARGETED_PIPELINE_FIELD, CampaignTypes, CampaignTypesEnum, DISPLAY_NAME_OVERRIDES, HelpTexts, placeHolderTexts, RecommendationTypes, RecommendationTypeEnum, DEFAULT_RECOMMENDATION_TYPE, RECOMMENDATION_TYPE_ATTRIBUTE, TARGET_AREA_ATTRIBUTE, SPA_CAMPAIGN, SPA_CAMPAIGN_LABEL, SPA_CAMPAIGN_TYPE_ATTRIBUTE } from "../../SPAConstants";
import { useSelector } from "react-redux";
import { permissionsSelector } from "../../../../shared-components/role-based-access-control/RoleBasedAccessControl.selectors.redux";
import { AppPersona } from "../../../../model/app-persona/AppPersona.model";
import { RootState } from "../../../../root-redux/RootState";
import { UserMode } from "../../redux/CacheHandler.redux";
import { MAX_CAMPAIGN_LIMIT_PER_FISCAL_QUARTER } from "../../../../services/utils/Constants";
import { CampaignUserObjectsState } from "../../redux/CampaignUserObjects.redux";
import { userObject } from "../../../../model/user-object/UserObject.model";
import { CustomCallout } from "./CustomCallout";

type stateType = { taxonomyDetails: { [key: string]: { [key: string]: Set<string> } }, status: RequestStatus };


interface IConfigRenderer {
    summary: { [key: string]: string | undefined },
    layoutConfigs: LayoutFields[] | undefined,
    selectionChange: (event: any) => boolean,
    taxonomyState: stateType,
    campaignUserObjectsState: CampaignUserObjectsState,
    section: number,
    editCampaignFlag: boolean
    campaignRules?: any
}

interface IMandatoryFields {
    selectionChange: (event: any) => boolean,
    layoutConfigs: LayoutFields[] | undefined,
    summary: { [key: string]: string | undefined },
    editCampaignFlag: boolean
}

interface IReleaseOnlyProdFields {
    selectionChange: (event: any) => boolean,
    layoutConfigs: LayoutFields[] | undefined,
    summary: { [key: string]: string | undefined },
    editCampaignFlag: boolean,
    section: number,
    taxonomyState: stateType,
    campaignUserObjectsState: CampaignUserObjectsState,
    campaignRules?: any
}

const HtmlContent = (htmlString: string) => {
    return <div dangerouslySetInnerHTML={{ __html: htmlString }} />;
}

const ValidateAndAllow = (summary: { [key: string]: string | undefined }, field: any, campaignRules: any) => {
    const campaignType = summary['CampaignType'];
    const solutionPlay = field as string;
    if (!campaignType || !solutionPlay) return true;
    return campaignRules?.[campaignType]?.[solutionPlay] < MAX_CAMPAIGN_LIMIT_PER_FISCAL_QUARTER
        || campaignRules?.[campaignType]?.[solutionPlay] === undefined;
}


const getEditOptions = (summary: { [key: string]: string | undefined }, layoutConfig: LayoutFields, taxonomyState: stateType, campaignRules: any, campaignUserObjectsState: CampaignUserObjectsState): ReactNode => {
    const fieldOptions = Array.from((((taxonomyState.taxonomyDetails[layoutConfig.taxonomyKey!] || {})[layoutConfig.name!]) || new Set<String>()).values())
        .filter(f => ((f as string) || "").length > 0)
        .sort();

    //For ApprovedBy Field,dropdown rendering doesnt happen from taxonomy 
    if(layoutConfig.name === CAMPAIGN_APPROVED_BY_FIELD){       
        if( !summary[SPA_CAMPAIGN_TYPE_ATTRIBUTE] || Helper.isEmpty(summary[SPA_CAMPAIGN_TYPE_ATTRIBUTE]) || campaignUserObjectsState == undefined || !campaignUserObjectsState.enterpriseStatus || campaignUserObjectsState.enterpriseStatus !== RequestStatus.succeeded) {
                       return <></>;
        }
           // Combine both enterprise and SMC approvers into one array
        const allApprovers: userObject[] = [
                ...(campaignUserObjectsState ? campaignUserObjectsState.enterpriseApprovers || [] : []),
                ...(campaignUserObjectsState ? campaignUserObjectsState.smcApprovers || [] : [])
            ].filter((approver, index, self) => 
                index === self.findIndex(a => a.id === approver.id)
            );
 
       const approvers : userObject[] = summary[SPA_CAMPAIGN_TYPE_ATTRIBUTE] === CampaignTypesEnum.Enterprise || summary[SPA_CAMPAIGN_TYPE_ATTRIBUTE] === CampaignTypesEnum.DSE ?
            (campaignUserObjectsState ? campaignUserObjectsState.enterpriseApprovers || [] : []) : (summary[SPA_CAMPAIGN_TYPE_ATTRIBUTE] === CampaignTypesEnum.SMC? (campaignUserObjectsState ? campaignUserObjectsState.smcApprovers || [] : []):allApprovers);
     
            return approvers.map(approver => (
            <Option
                key={approver.userPrincipalName} 
                selected={summary[layoutConfig.name] === approver.id }
                value={approver.id}
            >
                {approver.userPrincipalName}
            </Option>
    ));
    }
    // Simple rendering for all dropdowns except "SolutionPlay"
    if (layoutConfig.name !== 'SolutionPlay') {
        return fieldOptions.map(field => (
            <Option
                key={field as string}
                selected={summary[layoutConfig.name] === (field as string)}
                value={field as string}
            >
                {field as string}
            </Option>
        ));
    }

    // Complex rendering with disabled options and tooltips for "Conversation" dropdown
    return fieldOptions.map(field => {
        const isDisabled = !ValidateAndAllow(summary, field, campaignRules);
        const optionElem = (
            <Option
                key={field as string}
                selected={summary[layoutConfig.name] === (field as string)}
                disabled={isDisabled}
                value={field as string}
                title={isDisabled ? `Already created maximum allowed ${MAX_CAMPAIGN_LIMIT_PER_FISCAL_QUARTER} campaigns with this Conversation and Segment combination in current fiscal quarter.` : undefined}
            >
                {field as string}
            </Option>
        );
        return optionElem;
    });

}

const getFieldValue = (summary: any, field: string, type: string | undefined = undefined) => {
    return (summary && summary[field]) ? summary[field] : (type === 'date' ? undefined : '');
}

export const validateField = (pattern: string | undefined, value: string | undefined) => {
    if (pattern) {
        return (value || "").match(pattern);
    }
    return value && value.length > 0;
}

class UIControls {
    static isByPassLogicUnChecked = (summary: { [key: string]: string | undefined }, layoutConfig: LayoutFields) => {
        return layoutConfig.name === "ByPassCoolDownLogicJustification" && summary["Resurface"] === "false";
    }
    static isByPassLogicChecked = (summary: { [key: string]: string | undefined }, layoutConfig: LayoutFields) => {
        return layoutConfig.name === "ByPassCoolDownLogicJustification" && summary["Resurface"] === "true";
    }
}

const validateAndApply = (selectionChange: (event: any) => void, layoutConfigs: LayoutFields[] | undefined, event: any, errorMessage: string | undefined, isForRelease: boolean=false) => {
    const target = event.target;
    const pattern = (layoutConfigs || []).find(config => config.name === target.name)?.regex;
    if ((target.name === "CampaignName" && (target.value || "").includes('/')) || (!validateField(pattern, (target.value || "").trim()))) {
       //For RequiredOnlyForProd fields, we shouldnt show validation messages in summary page
        if (layoutConfigs && layoutConfigs.length > 0) {
            const  layoutConfig = layoutConfigs[0];
           layoutConfig.requiredOnlyForProd && !isForRelease?target.setCustomValidity(''): target.setCustomValidity(errorMessage);
        }
    } else {
        target.setCustomValidity('');
    }
    selectionChange(event);
}

const generateElements = (summary: { [key: string]: string | undefined }, layoutConfig: LayoutFields, taxonomyState: stateType, editCampaignFlag: boolean, isForRelease: boolean, campaignRules: any,campaignUserObjectsState: CampaignUserObjectsState, selectionChange: (event: any) => void): ReactNode => {
    const todayDate = new Date().toISOString().split('T')[0];

    if (taxonomyState.status !== RequestStatus.succeeded || Helper.isEmpty(taxonomyState.taxonomyDetails)) {
        return <></>;
    }

    if (layoutConfig.type === 'textbox') {
        const isNumericField = layoutConfig.name === CAMPAIGN_TARGETED_PIPELINE_FIELD;
        const rawValue = getFieldValue(summary, layoutConfig.name);
        return (
            <td>
                <TextField  name={layoutConfig.name} 
                            label={layoutConfig.name} 
                            className={'Text-fields'} 
                            placeholder={placeHolderTexts[layoutConfig.name] || `Enter ${layoutConfig.displayValue} here`} 
                            value={isNumericField ? Helper.formatNumberWithCommas(rawValue) : rawValue} 
                            disabled={editCampaignFlag && !layoutConfig.editable} 
                            onHeChange={(event) => {
                                if (isNumericField) { (event.target as any).value = Helper.stripCommas((event.target as any).value); }
                                validateAndApply(selectionChange, [layoutConfig], event, (layoutConfig.validationMessage?.length || 0) > 0 ? layoutConfig.displayValue + " " + layoutConfig.validationMessage : `${layoutConfig.displayValue} is less than (5) character in length or has invalid character's`,isForRelease);
                            }} />
            </td>
        )
    }
    else if (layoutConfig.type === 'textarea') {
        var value = getFieldValue(summary, layoutConfig.name).replaceAll('\\\\\\\\r\\\\\\\\n', layoutConfig.name === 'RecommendationTitle' ? '\n' : '\n\n').replaceAll('$$', '$').replaceAll("\\\\'", "'");
        return (
            <td>
                <TextArea   name={layoutConfig.name} 
                            label={layoutConfig.name} 
                            className={'Text-fields'} 
                            placeholder={placeHolderTexts[layoutConfig.name] || `Enter ${layoutConfig.displayValue} here`} 
                            style={{ marginBottom: '30px' }} 
                            value={value} 
                            onHeChange={(event) => validateAndApply(selectionChange, [layoutConfig], event, (layoutConfig.validationMessage?.length || 0) > 0 ? layoutConfig.displayValue + " " + layoutConfig.validationMessage : `${layoutConfig.displayValue} is not within 5 to 2500 characters in length or has invalid character's`)} 
                            disabled={UIControls.isByPassLogicUnChecked(summary, layoutConfig)} />
            </td>
        )
    }
    else if (layoutConfig.type === "datetime") {
        return (
            <td className='spa-form'>
                <DatePicker label={layoutConfig.name} 
                            name={layoutConfig.name} 
                            className={'Text-fields spa-label-hider'} 
                            onHeChange={selectionChange} 
                            required={layoutConfig.required || (isForRelease && layoutConfig.requiredOnlyForProd)} 
                            value={getFieldValue(summary, layoutConfig.name, 'date')} 
                            disabled={editCampaignFlag && !layoutConfig.editable} 
                            maxDate={todayDate} 
                            onHeClear={selectionChange}/>
            </td>
        )
    }
    else if (layoutConfig.type === 'dropdown') {
        return (
            <td>
                <Select name={layoutConfig.name} 
                        label={layoutConfig.name} 
                        className={'Text-fields spa-label-hider'} 
                        onHeChange={selectionChange} 
                        required={layoutConfig.required} 
                        placeholder={placeHolderTexts[layoutConfig.name] || `Select ${layoutConfig.displayValue} here`} 
                        autocomplete="list" 
                        value={getFieldValue(summary, layoutConfig.name)} 
                        disabled={editCampaignFlag && !layoutConfig.editable}
                >
                    {getEditOptions(summary, layoutConfig, taxonomyState, campaignRules,campaignUserObjectsState)}
                </Select>
            </td>
        )
    } else if(layoutConfig.type === 'checkbox') {
        return (
            <td>
                <Checkbox 
                    name={layoutConfig.name} 
                    label={layoutConfig.displayValue}
                    style={{padding: '10px'}}
                    className={'Text-fields'} 
                    onHeChange={selectionChange} 
                    required={layoutConfig.required} 
                    placeholder={`Resurface Campaign`} 
                    checked={getFieldValue(summary, layoutConfig.name) === "true"}
                />
            </td>
        )
    } else if (layoutConfig.type === 'header') {
        return (
            <td colSpan={3}>
                <h5 aria-label={layoutConfig.displayValue} style={{borderBottom: '1px solid #f3f3f3', paddingBottom: '5px'}}>{layoutConfig.displayValue}:</h5>
            </td>
        )
    }
    return <></>;
}
export const RenderProdOnlyReleaseFields: React.FC<IReleaseOnlyProdFields> = ({ summary, section, layoutConfigs, taxonomyState, selectionChange, editCampaignFlag, campaignRules,campaignUserObjectsState }) => {
    return (
        <>
            {
                layoutConfigs && layoutConfigs.filter(layoutConfig => layoutConfig.section === section).map(layoutConfig => (
                    layoutConfig.requiredOnlyForProd &&
                    <tr key={layoutConfig.name}>
                        <td>
                            {DISPLAY_NAME_OVERRIDES[layoutConfig.name] || layoutConfig.displayValue} {layoutConfig.requiredOnlyForProd && <Icon className='required-icon' name='actioncenterasterisk'></Icon>}
                            <CustomCallout name={layoutConfig.name} message={HtmlContent(HelpTexts[`${layoutConfig.name}`])} />
                        </td>
                        {generateElements(summary, layoutConfig, taxonomyState, editCampaignFlag, true, campaignRules,campaignUserObjectsState, selectionChange)}
                    </tr>
                ))
            }
        </>
    )
}
export const RenderMandatoryFields: React.FC<IMandatoryFields> = ({ selectionChange, layoutConfigs, summary, editCampaignFlag }) => {

    const permissionsList = useSelector(permissionsSelector);
    const userSalesRoles = useSelector((state: RootState) => state.userSalesRoles);
    const isSuperAdmin = useSelector((state: RootState) => state.isSuperAdmin.isSuperAdmin);
    const allAppsList = useSelector((state: RootState): AppPersona[] => state.appPersonas.list);
    const campaignCache = useSelector((state: RootState) => state.cacheHandler.campaignCache);
    // Raw Taxonomy Central slice (MARKETING_GEO_LANG Area column) - deduped in backend. Read directly since this is a hardcoded (non config-driven) field.
    const marketingGeoAreas = useSelector((state: RootState) => (state.taxonomy?.taxonomyDetails?.marketingGeoArea ?? []) as string[]);
    // Target Area is mandatory only for Local Campaign recommendation type.
    const isLocalCampaign = (getFieldValue(summary, RECOMMENDATION_TYPE_ATTRIBUTE) || DEFAULT_RECOMMENDATION_TYPE) === RecommendationTypeEnum.LOCAL;

    const todayDate = new Date().toISOString().split('T')[0];
    const [actionByMinDate, setActionByMinDate] = useState<string | undefined>(undefined);
    const [expiryMinDate, setExpiryMinDate] = useState<string | undefined>(undefined);

    const userTeams = permissionsList.userTeams;
    const teamsList: [string, string][] = [];
    const allTeamsList: [string, string][] = [];

    userTeams.name.forEach((element: string) => teamsList.push([userTeams.key[userTeams.name.indexOf(element)], element]));
    allAppsList.forEach((app: AppPersona) => allTeamsList.push([app.subscriptionKey, app.appName]));

    const startOnDateReady = (e: any) => {
        if (e.target.value === undefined) return;
        selectionChange(e);
        const temp = new Date(e.target.value);
        temp.setDate(temp.getDate() + 2);
        setActionByMinDate(temp.toISOString().split('T')[0]);
    }

    const actionByDateReady = (e: any) => {
        if (e.target.value === undefined) return;
        selectionChange(e);
        const temp = new Date(e.target.value);
        temp.setDate(temp.getDate() + 2);
        setExpiryMinDate(temp.toISOString().split('T')[0]);
    }

    useEffect(() => {
        let temp = summary["StartOn"] ? new Date(summary["StartOn"]) : new Date();
        temp.setDate(temp.getDate() + 2);
        setActionByMinDate(temp.toISOString().split('T')[0]);
        temp = summary["ActionByDate"] ? new Date(summary["ActionByDate"]) : new Date();
        temp.setDate(temp.getDate() + 2);
        setExpiryMinDate(temp.toISOString().split('T')[0]);
    });

    return (
        <>
            <tr>
                <td colSpan={3}>
                    <h5 aria-label="Seller facing details" style={{borderBottom: '1px solid #f3f3f3', paddingBottom: '5px'}}>Seller facing details:</h5>
                </td>
            </tr>
            <tr>
                <td>
                    CA Campaign Name <Icon className='required-icon' name='actioncenterasterisk'></Icon>
                    <CustomCallout name={'CampaignName'} message={HtmlContent(HelpTexts.CampaignName)}/>
                </td>
                <td>
                    <TextField  name={'CampaignName'} 
                                label={'CampaignName'} 
                                className={'Text-fields'} 
                                placeholder={`Provide Campaign name in format: FY26_Q1_<NAME>`} 
                                value={getFieldValue(summary, 'CampaignName')} 
                                onHeChange={(event) => validateAndApply(selectionChange, layoutConfigs, event, "Campaign Name must include at least 5 letters or numbers in a row. '/' is not allowed.",false)} />
                </td>
            </tr>
            <tr>
                <td>
                    CA/Campaign Tag <Icon className='required-icon' name='actioncenterasterisk'></Icon>
                    <CustomCallout name={'Tags'} message={HtmlContent(HelpTexts.Tags)} />
                </td>
                <td className='spa-form'>
                    <TextField  name={'Tags'} 
                                label={'CampaignName'} 
                                className={'Text-fields'} 
                                placeholder={`Enter Campaign Tag here`} 
                                value={getFieldValue(summary, 'Tags')} 
                                disabled={editCampaignFlag} 
                                onHeChange={(event) => validateAndApply(selectionChange, layoutConfigs, event, "Tag must be at least of length 3")} />
                </td>
            </tr>
            <tr style={{ display: userSalesRoles.isSpaUser ? 'none' : 'contents' }}>
                <td>
                    Tenant <Icon className='required-icon' name='actioncenterasterisk'></Icon>
                    <CustomCallout name={'Tenant'} message={<>Team names Ex: SMC, PIE, Accounts 360. A user can be part of multiple teams.</>} />
                </td>
                <td>
                    <Select
                        label={'Tenant'}
                        disabled={userSalesRoles.isSpaUser || editCampaignFlag}
                        value={getFieldValue(summary, 'SubscriptionKey') || SPA_CAMPAIGN}
                        slot='action'
                        fixed-placement
                        className='Text-fields spa-label-hider'
                        placeholder={'Select Tenant here'}
                        name='SubscriptionKey'
                        onHeChange={selectionChange}
                        required
                    >
                        {isSuperAdmin ?
                            allTeamsList.map(item => (
                                <Option
                                    key={item[0]}
                                    role='menuitemcheckbox'
                                    value={item[0]}
                                >
                                    {item[1] === SPA_CAMPAIGN ? SPA_CAMPAIGN_LABEL : item[1]}
                                </Option>
                            ))
                            :
                            teamsList.map(item => (
                                <Option
                                    key={item[0]}
                                    role='menuitemcheckbox'
                                    value={item[0]}
                                    selected={item[1] === SPA_CAMPAIGN || item[0] === summary['SubscriptionKey']}
                                >
                                    {item[1] === SPA_CAMPAIGN ? SPA_CAMPAIGN_LABEL : item[1]}
                                </Option>
                            ))
                        }
                    </Select>
                </td>
            </tr>
            <tr>
                <td>
                    Recommendation Type <Icon className='required-icon' name='actioncenterasterisk'></Icon>
                    <CustomCallout name={'RecommendationType'} message={HtmlContent(HelpTexts.RecommendationType)} />
                </td>
                <td>
                    <Select name={RECOMMENDATION_TYPE_ATTRIBUTE} label='Recommendation Type' className={'Text-fields'} onHeChange={selectionChange} placeholder={`Select Recommendation Type here`} autocomplete="list" value={getFieldValue(summary, RECOMMENDATION_TYPE_ATTRIBUTE) || DEFAULT_RECOMMENDATION_TYPE} disabled={editCampaignFlag || campaignCache?.mode === UserMode.EDIT}>
                        {
                            RecommendationTypes.map(optionValue => (
                                <Option selected={(summary[RECOMMENDATION_TYPE_ATTRIBUTE] || DEFAULT_RECOMMENDATION_TYPE) === optionValue[0]} value={optionValue[0]}>{optionValue[1]}</Option>
                            ))
                        }
                    </Select>
                </td>
            </tr>
            {isLocalCampaign &&
            <tr>
                <td>
                    Target Area <Icon className='required-icon' name='actioncenterasterisk'></Icon>
                    <CustomCallout name={'TargetArea'} message={HtmlContent(HelpTexts.TargetArea)} />
                </td>
                <td>
                    <Select name={TARGET_AREA_ATTRIBUTE} label='Target Area' className={'Text-fields'} onHeChange={selectionChange} placeholder={`Select Target Area here`} autocomplete="list" value={getFieldValue(summary, TARGET_AREA_ATTRIBUTE)} required disabled={editCampaignFlag || campaignCache?.mode === UserMode.EDIT}>
                        {
                            marketingGeoAreas.map(area => (
                                <Option key={area} selected={summary[TARGET_AREA_ATTRIBUTE] === area} value={area}>{area}</Option>
                            ))
                        }
                    </Select>
                </td>
            </tr>
            }
            <tr>
                <td>
                    Go-Live Date <Icon className='required-icon' name='actioncenterasterisk'></Icon>
                    <CustomCallout name={'StartOn'} message={HtmlContent(HelpTexts.StartOn)} />
                </td>
                <td className='spa-form'>
                    <DatePicker name={'StartOn'} 
                                className={'Text-fields spa-label-hider'} 
                                label="Go Live Date" 
                                onHeChange={startOnDateReady} 
                                min-date={todayDate} 
                                required={true} 
                                value={getFieldValue(summary, 'StartOn', 'date')} 
                                disabled={editCampaignFlag} />
                </td>
            </tr>
            <tr>
                <td>
                    Action-by Date <Icon className='required-icon' name='actioncenterasterisk'></Icon>
                    <CustomCallout name={'ActionByDate'} message={HtmlContent(HelpTexts.ActionByDate)} />
                </td>
                <td className='spa-form'>
                    <DatePicker name={'ActionByDate'} 
                                className={'Text-fields spa-label-hider'} 
                                label="Action by Date" 
                                onHeChange={actionByDateReady} 
                                min-date={actionByMinDate} 
                                required={true} 
                                value={getFieldValue(summary, 'ActionByDate', 'date')} 
                                disabled={actionByMinDate === undefined} />
                </td>
            </tr>
            <tr>
                <td>
                    Expiry Date <Icon className='required-icon' name='actioncenterasterisk'></Icon>
                    <CustomCallout name={'ExpiryDate'} message={HtmlContent(HelpTexts.ExpiryDate)} />
                </td>
                <td className='spa-form'>
                    <DatePicker name={'ExpiryDate'} 
                                label="Expiry Date" 
                                className={'Text-fields spa-label-hider'} 
                                onHeChange={selectionChange} 
                                disabled={(expiryMinDate === undefined)} 
                                minDate={expiryMinDate} 
                                required={true} 
                                value={getFieldValue(summary, 'ExpiryDate', 'date')} />
                </td>
            </tr>
            <tr>
                <td>
                    Target Segment <Icon className='required-icon' name='actioncenterasterisk'></Icon>
                    <CustomCallout name={'TypeOfCampaign'} message={HtmlContent(HelpTexts.TypeOfCampaign)} />
                </td>
                <td>
                    <Select name='CampaignType' label='Campaign Type' className={'Text-fields'} onHeChange={selectionChange} placeholder={`Select Campaign Type here`} autocomplete="list" value={getFieldValue(summary, "CampaignType") || CampaignTypes[0]} disabled={editCampaignFlag || campaignCache?.mode === UserMode.EDIT}>
                        {
                            CampaignTypes.filter(optionValue => optionValue[0] !== CampaignTypesEnum.DSE || summary["CampaignType"] === CampaignTypesEnum.DSE).map(optionValue => (
                                <Option selected={summary["CampaignType"] === optionValue[0]} value={optionValue[0]}>{optionValue[1]}</Option>
                            ))
                        }
                    </Select>
                </td>
            </tr>
        </>
    )
}

const ConfigRendererComponent: React.FC<IConfigRenderer> = ({ summary, section, layoutConfigs, selectionChange, taxonomyState, editCampaignFlag, campaignRules, campaignUserObjectsState }) => {
    return (
        <>
            {
                layoutConfigs && layoutConfigs.filter(layoutConfig => layoutConfig.section === section).map(layoutConfig => (
                    layoutConfig.type !== "hidden" &&
                    <tr>
                        {layoutConfig.type !== "header" &&
                            <td style={{color: UIControls.isByPassLogicUnChecked(summary, layoutConfig) ? 'silver' : 'black'}}>
                                {DISPLAY_NAME_OVERRIDES[layoutConfig.name] || layoutConfig.displayValue} {(layoutConfig.required || UIControls.isByPassLogicChecked(summary, layoutConfig)) && <Icon className='required-icon' name='actioncenterasterisk'></Icon>}
                                <CustomCallout name={layoutConfig.name} message={HtmlContent(HelpTexts[`${layoutConfig.name}`])} displayValue={UIControls.isByPassLogicUnChecked(summary, layoutConfig) ? 'none': ''}  />
                            </td>
                        }
                        {generateElements(summary, layoutConfig, taxonomyState, editCampaignFlag, false, campaignRules,campaignUserObjectsState,selectionChange)}
                    </tr>
                ))
            }
        </>
    )
}

export const ConfigRenderer = ConfigRendererComponent;